import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import MaxWidthContainer from '../../_atoms/MaxWidthContainer';

const MOBILE = '(max-width: 992px)';
const IMG_BASE = '/curriculum-bluebonnet';
const LEAD_TYPEFORM_URL = 'https://9uq31r57i3q.typeform.com/to/oyTyfiVe';

export type BluebonnetVariant = 'default' | 'lead' | 'thank-you';

export interface BluebonnetLandingContentProps {
  variant?: BluebonnetVariant;
  isAuthenticated?: boolean;
}

const BluebonnetLandingContent: React.FC<BluebonnetLandingContentProps> = ({
  variant = 'default',
  isAuthenticated = false,
}) => {
  return (
    <>
      {/* ───────────────── 1. HERO ───────────────── */}
      <HeroSection>
        <HeroBlobOne aria-hidden="true" />
        <HeroBlobTwo aria-hidden="true" />
        <MaxWidthContainer>
          <HeroLayout>
            <HeroLeft>
              <HeroTitle>
                The Curipod + Bluebonnet Classroom
                <HeroTitleAccentLine>
                  <HeroTitleAccent>Where the curriculum comes alive.</HeroTitleAccent>
                </HeroTitleAccentLine>
              </HeroTitle>
              <HeroSubtitle>
                Bluebonnet brings structure and rigor to K-5 Texas RLA classrooms. Curipod
                brings the dialogue, the curiosity, and the deep engagement with content.
                A powerful complement to the resources your teachers are already using.
              </HeroSubtitle>
              <HeroCta variant={variant} />
            </HeroLeft>

            <HeroRight>
              <HeroImageFrame>
                <HeroImage
                  src={`${IMG_BASE}/image1.jpg`}
                  alt="A Bluebonnet lesson running on a Curipod activity slide in a K-5 Texas classroom, students engaged, looking up at the screen."
                />
              </HeroImageFrame>
            </HeroRight>
          </HeroLayout>
        </MaxWidthContainer>
      </HeroSection>

      {/* ───────────────── 2. HOW CURIPOD BRINGS BLUEBONNET TO LIFE ───────────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <NarrowCenter>
            <SectionTag>HOW CURIPOD BRINGS BLUEBONNET TO LIFE</SectionTag>
            <SectionTitle>A Strong Curriculum Deserves Strong Classrooms.</SectionTitle>
            <SectionText>
              Bluebonnet gives Texas districts a rigorous, well-sequenced K-5 RLA
              curriculum. Curipod gives every Bluebonnet lesson moments of engagement,
              discussion, and student voice. Great content becomes a living classroom, and
              teachers never leave the curriculum they&apos;re delivering.
            </SectionText>
            <SectionText>
              Working with Texas literacy teams, we&apos;ve identified the repeatable
              moments inside every Bluebonnet lesson where engagement matters most and
              built interactive formats to fit.
            </SectionText>
          </NarrowCenter>

          <FitColumnGrid>
            <FitColumnCard>
              <FitColumnTitle>Tiered Vocabulary Practice</FitColumnTitle>
              <FitColumnBody>
                Rapid practice on every word a Bluebonnet lesson introduces — general,
                academic, and skill-specific — plus deep engagement with the focus word at
                the heart of each lesson.
              </FitColumnBody>
              <FitImageRow>
                <FitProductImage
                  src={`${IMG_BASE}/image2.png`}
                  alt="Curipod vocabulary slide with the lesson's focus word and an interactive prompt"
                  loading="lazy"
                />
                <FitProductImage
                  src={`${IMG_BASE}/image3.png`}
                  alt="Curipod vocabulary practice slide showing real Bluebonnet content"
                  loading="lazy"
                />
              </FitImageRow>
            </FitColumnCard>

            <FitColumnCard>
              <FitColumnTitle>Foundational Skill Practice</FitColumnTitle>
              <FitColumnBody>
                Bluebonnet builds core K-5 skills, phonics, comprehension, grammar,
                figurative language. Curipod adds the interactive practice that makes them
                stick, with every student getting the reps.
              </FitColumnBody>
              <FitImageRow>
                <FitProductImage
                  src={`${IMG_BASE}/image4.png`}
                  alt="Curipod skill-practice slide with AI feedback on a student response"
                  loading="lazy"
                />
              </FitImageRow>
            </FitColumnCard>

            <FitColumnCard>
              <FitColumnTitle>Read-Aloud &amp; Exit Tickets</FitColumnTitle>
              <FitColumnBody>
                Read-alouds and exit tickets become moments where every student writes,
                reflects, and connects ideas. Understanding builds deeper, student by
                student, idea by idea.
              </FitColumnBody>
              <FitImageRow>
                <FitProductImage
                  src={`${IMG_BASE}/image5.png`}
                  alt="Curipod read-aloud comprehension check"
                  loading="lazy"
                />
                <FitProductImage
                  src={`${IMG_BASE}/image6.png`}
                  alt="Curipod exit ticket slide showing student responses"
                  loading="lazy"
                />
              </FitImageRow>
            </FitColumnCard>
          </FitColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* ───────────────── 3. THREE PILLARS ───────────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <NarrowCenter>
            <SectionTag>THREE PILLARS</SectionTag>
            <SectionTitle>Why the Curipod + Bluebonnet Classroom Works.</SectionTitle>
          </NarrowCenter>

          <PillarGrid>
            <PillarCard>
              <PillarNumber>01</PillarNumber>
              <PillarTitle>
                Fidelity to the Curriculum. Alive in the Classroom.
              </PillarTitle>
              <PillarBody>
                Every Curipod + Bluebonnet format is mapped to the unit, the lesson, and
                the standard. Teachers don&apos;t replan, they plug in Curipod where
                engagement matters most. Curriculum teams get a coherent rollout: the same
                curriculum, alive in every classroom.
              </PillarBody>
              <PillarImage
                src={`${IMG_BASE}/image7.png`}
                alt="A Bluebonnet unit map and workbook spread"
                loading="lazy"
              />
            </PillarCard>

            <PillarCard>
              <PillarNumber>02</PillarNumber>
              <PillarTitle>Real-Time AI Feedback That Moves Learning Forward</PillarTitle>
              <PillarBody>
                Every student gets immediate, specific, rubric-aligned feedback while the
                learning is still fresh — not a week later in a workbook review. Decades
                of research name timely, specific feedback as one of the highest-leverage
                moves in instruction (Hattie&apos;s effect size of 0.79). Curipod brings
                it to every Bluebonnet lesson — every student, every time.
              </PillarBody>
              <PillarImage
                src={`${IMG_BASE}/image8.png`}
                alt="Close-up of a student mid-thought, typing a response on a Curipod activity"
                loading="lazy"
              />
            </PillarCard>

            <PillarCard>
              <PillarNumber>03</PillarNumber>
              <PillarTitle>Built in Partnership With Texas RLA Educators</PillarTitle>
              <PillarBody>
                Designed hand-in-hand with literacy directors and teachers who live
                Bluebonnet every day, including the team at Dickinson ISD. Grounded in the
                learning science embedded in every Curipod lesson.
              </PillarBody>
              <PillarImageStack>
                <PillarImage
                  src={`${IMG_BASE}/image9.jpg`}
                  alt="A teacher at the front of the room facilitating a Curipod activity"
                  loading="lazy"
                />
                <PillarImage
                  src={`${IMG_BASE}/image10.jpg`}
                  alt="Texas teachers working with Curipod alongside Bluebonnet curriculum"
                  loading="lazy"
                />
              </PillarImageStack>
            </PillarCard>
          </PillarGrid>
        </MaxWidthContainer>
      </Section>

      {/* ───────────────── 4. THE DICKINSON STORY ───────────────── */}
      <DickinsonSection id="dickinson-story">
        <MaxWidthContainer>
          <DickinsonHeader>
            <DickinsonTag>THE DICKINSON STORY</DickinsonTag>
            <DickinsonTitle>
              How Dickinson ISD is Bringing Bluebonnet to Life.
            </DickinsonTitle>
            <DickinsonMeta>
              Dickinson ISD, Galveston County, Texas, Bluebonnet Learning RLA, K-5
            </DickinsonMeta>
          </DickinsonHeader>

          <DickinsonBodyWrap>
            <DickinsonChapterTag>The Partnership</DickinsonChapterTag>
            <DickinsonBody>
              Dickinson ISD is adopting Bluebonnet Learning RLA for the 2026-27 school
              year, strong, well-sequenced content built for Texas classrooms. To make
              sure every K-5 lesson lands with the energy Dickinson&apos;s teachers and
              students deserve, the district partnered with Curipod.
            </DickinsonBody>
            <DickinsonBody>
              Together with Jennifer Brewer, Dickinson&apos;s K-12 Literacy Coordinator,
              and her literacy team, we sat down with the curriculum unit by unit. The
              question we kept returning to:{' '}
              <em>where does engagement need the most help?</em> Vocabulary. Skill
              practice. Read-alouds. Exit tickets. We mapped the repeatable moments inside
              a Bluebonnet lesson and built Curipod activity formats to fit, quick to drop
              into the scope and sequence, easy to facilitate, always in service of the
              curriculum.
            </DickinsonBody>

            <DickinsonQuoteLayout>
              <DickinsonPullQuote $variant="highlight">
                <DickinsonQuoteMark>&ldquo;</DickinsonQuoteMark>
                <DickinsonPullQuoteText>
                  It&apos;s not the curriculum. It&apos;s not the assignment. It&apos;s
                  your teacher. That&apos;s what changes kids.
                </DickinsonPullQuoteText>
                <DickinsonPullQuoteAttrib>
                  Jennifer Brewer, K-12 Literacy Coordinator, Dickinson ISD
                </DickinsonPullQuoteAttrib>
              </DickinsonPullQuote>
              <DickinsonPortrait
                src={`${IMG_BASE}/image11.jpg`}
                alt="Jennifer Brewer, K-12 Literacy Coordinator at Dickinson ISD"
                loading="lazy"
              />
            </DickinsonQuoteLayout>

            <DickinsonChapterTag>What Changed in Dickinson</DickinsonChapterTag>
            <DickinsonBody>
              When Curipod activities started rolling out, the district saw what happens
              when Bluebonnet content gets a room talking. Kids leaning in. Writing more.
              Asking sharper questions. Staying with hard ideas longer than expected.
            </DickinsonBody>
            <DickinsonBody>
              Together with Dickinson, we&apos;ve built a growing library of Curipod
              activity formats, designed to fit the rhythm of every Bluebonnet K-5 lesson,
              quick to drop into the scope and sequence, easy to facilitate, always in
              service of the curriculum.
            </DickinsonBody>

            <FormatCallout>
              Including Exit Tickets with Real-Time Feedback, Vocabulary Hustles, Skill
              Mini-Lessons, and Read-Aloud Comprehension Moments, with more in build.
            </FormatCallout>

            <DickinsonBody>
              Dickinson is now moving forward with Curipod + Bluebonnet across the
              district, using the formats we&apos;ve built together to bring energy,
              dialogue, and depth to every lesson.
            </DickinsonBody>
          </DickinsonBodyWrap>
        </MaxWidthContainer>
      </DickinsonSection>

      {/* ───────────────── 5. PROOF ACROSS TEXAS ───────────────── */}
      <ProofSection>
        <MaxWidthContainer>
          <NarrowCenter>
            <ProofTitle>Classrooms That Use Curipod Get Results.</ProofTitle>
          </NarrowCenter>

          <StatStrip>
            <Link href="/case-studies/curipod-case-study-south-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +22<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Meets &amp; Masters on STAAR</StatCardLabel>
                  <StatCardSub>South Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-south-east-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +32<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Growth in STAAR scores</StatCardLabel>
                  <StatCardSub>South East Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-sivells-bend" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    C <StatArrow>→</StatArrow> A
                  </StatBigNumber>
                  <StatCardLabel>Rating in one year</StatCardLabel>
                  <StatCardSub>Sivells Bend ISD</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-ysleta" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +43<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>A 4th grade transformation</StatCardLabel>
                  <StatCardSub>Corpus Christi, TX</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-flour-bluff" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +20<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Increase in STAAR RLA Passing Rate</StatCardLabel>
                  <StatCardSub>Schertz-Cibolo-Universal City ISD</StatCardSub>
                </StatCard>
              </a>
            </Link>
          </StatStrip>
        </MaxWidthContainer>
      </ProofSection>

      {/* ───────────────── 6. FINAL DUAL CTA ───────────────── */}
      <FinalCtaSection>
        <MaxWidthContainer>
          <FinalCtaInner>
            <FinalCtaImageWrap>
              <FinalCtaImage
                src={`${IMG_BASE}/image9.jpg`}
                alt="A Texas classroom using Curipod with Bluebonnet"
                loading="lazy"
              />
            </FinalCtaImageWrap>
            <FinalCtaContent>
              <FinalCtaTitle>
                See Bluebonnet the Way Your Students Will Remember It.
              </FinalCtaTitle>
              <FinalCtaButtonRow>
                <FinalCtaGroup>
                  <FinalCtaGroupLabel>For district leaders</FinalCtaGroupLabel>
                  <Link href="/quotes" passHref>
                    <a>
                      <FinalCtaPrimaryButton type="primary">
                        Get a Quote
                      </FinalCtaPrimaryButton>
                    </a>
                  </Link>
                </FinalCtaGroup>
                <FinalCtaGroup>
                  <FinalCtaGroupLabel>For teachers</FinalCtaGroupLabel>
                  <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                    <a>
                      <FinalCtaSecondaryButton>Try Curipod free</FinalCtaSecondaryButton>
                    </a>
                  </Link>
                </FinalCtaGroup>
              </FinalCtaButtonRow>
              <FinalCtaSignoff>
                Made with <FinalCtaHeart>🧡</FinalCtaHeart> from Norway. Built with Texas
                educators.
              </FinalCtaSignoff>
            </FinalCtaContent>
          </FinalCtaInner>
        </MaxWidthContainer>
      </FinalCtaSection>
    </>
  );
};

export default BluebonnetLandingContent;

/* ─── HERO CTA (varies by variant) ────────────────────────────────────────── */

interface HeroCtaProps {
  variant: BluebonnetVariant;
}

const HeroCta: React.FC<HeroCtaProps> = ({ variant }) => {
  if (variant === 'lead') {
    return (
      <HeroCtaRow>
        <HeroPrimaryButtonLink href={LEAD_TYPEFORM_URL}>
          <HeroPrimaryButton type="primary">
            Leave your email to get more information
          </HeroPrimaryButton>
        </HeroPrimaryButtonLink>
        <HeroSecondaryLink href="#dickinson-story">
          See the Dickinson Story →
        </HeroSecondaryLink>
      </HeroCtaRow>
    );
  }

  if (variant === 'thank-you') {
    return (
      <HeroThankYouCard role="status" aria-live="polite">
        <HeroThankYouTitle>Thanks — we got it.</HeroThankYouTitle>
        <HeroThankYouBody>
          Someone from the Curipod team will be in touch shortly with more information
          about Curipod + Bluebonnet for your classroom or district.
        </HeroThankYouBody>
      </HeroThankYouCard>
    );
  }

  return (
    <HeroCtaRow>
      <Link href="/quotes" passHref>
        <a>
          <HeroPrimaryButton type="primary">
            Book a demo, see it in action
          </HeroPrimaryButton>
        </a>
      </Link>
      <HeroSecondaryLink href="#dickinson-story">
        See the Dickinson Story →
      </HeroSecondaryLink>
    </HeroCtaRow>
  );
};

/* ─── 1. HERO ─────────────────────────────────────────────────────────────── */

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  color: ${theme.colors.white};
  padding: 100px 20px 90px;
  overflow: hidden;

  @media ${MOBILE} {
    padding: 80px 16px 64px;
  }
`;

const HeroBlobOne = styled.div`
  position: absolute;
  top: -180px;
  right: -140px;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
`;

const HeroBlobTwo = styled.div`
  position: absolute;
  bottom: -120px;
  left: -100px;
  width: 460px;
  height: 460px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  pointer-events: none;
`;

const HeroLayout = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroLeft = styled.div`
  position: relative;
`;

const HeroRight = styled.div`
  position: relative;

  @media ${MOBILE} {
    order: -1;
  }
`;

const HeroImageFrame = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 6px rgba(255, 255, 255, 0.18);
  background: ${theme.colors.white};
  aspect-ratio: 4 / 3;

  @media ${MOBILE} {
    aspect-ratio: 3 / 2;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroTitle = styled.h1`
  font-size: 60px;
  line-height: 1.05;
  font-weight: 900;
  color: ${theme.colors.white};
  margin: 0 0 20px;
  letter-spacing: -1.2px;

  @media (max-width: 1200px) {
    font-size: 52px;
  }

  @media ${MOBILE} {
    font-size: 42px;
  }

  @media (max-width: 420px) {
    font-size: 32px;
  }
`;

const HeroTitleAccentLine = styled.span`
  display: block;
  margin-top: 6px;
`;

const HeroTitleAccent = styled.span`
  font-weight: 500;
  font-style: italic;
  color: #fff3e0;
  letter-spacing: -0.5px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.92);
  max-width: 640px;
  line-height: 1.65;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 16px;
  }
`;

const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
`;

const HeroPrimaryButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 36px;
  font-size: 17px;
  height: 56px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.32);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }
`;

const HeroSecondaryLink = styled.a`
  color: ${theme.colors.white};
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  padding-bottom: 2px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-bottom-color: ${theme.colors.white};
    transform: translateX(2px);
  }
`;

const HeroPrimaryButtonLink = styled.a`
  display: inline-block;
  text-decoration: none;
`;

const HeroThankYouCard = styled.div`
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  padding: 24px 28px;
  max-width: 560px;
  color: ${theme.colors.white};
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
`;

const HeroThankYouTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  margin: 0 0 8px;
  color: ${theme.colors.white};
  letter-spacing: -0.3px;
`;

const HeroThankYouBody = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
`;

/* ─── SHARED SECTION PRIMITIVES ────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 90px 0;

  @media ${MOBILE} {
    padding: 56px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2.2px;
  margin-bottom: 14px;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 22px;
  line-height: 1.15;
  letter-spacing: -0.6px;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  margin: 0 0 18px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const NarrowCenter = styled.div`
  max-width: 820px;
  margin: 0 auto 56px;
  text-align: center;

  @media ${MOBILE} {
    margin-bottom: 36px;
  }
`;

/* ─── 2. HOW CURIPOD BRINGS BLUEBONNET TO LIFE ─────────────────────────────── */

const FitColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const FitColumnCard = styled.div`
  position: relative;
  background: ${theme.colors.white};
  border-radius: 10px;
  padding: 28px 26px;
  border: 1px solid rgba(232, 123, 39, 0.18);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(26, 21, 48, 0.08);
  }
`;

const FitColumnTitle = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 12px;
  line-height: 1.2;
`;

const FitColumnBody = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.65;
  margin: 0 0 20px;
`;

const FitImageRow = styled.div`
  position: relative;
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
`;

const FitProductImage = styled.img`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  border-radius: 8px;
  background: ${theme.colors.paleBeige};
  object-fit: contain;
  display: block;
  border: 1px solid rgba(232, 123, 39, 0.18);
`;

/* ─── 3. PILLARS ─────────────────────────────────────────────────────────── */

const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const PillarCard = styled.div`
  background: ${theme.colors.paleBeige};
  border-radius: 10px;
  padding: 32px 28px;
  border: 1px solid rgba(232, 123, 39, 0.14);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(26, 21, 48, 0.08);
  }
`;

const PillarNumber = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.orange};
  letter-spacing: 2px;
  margin-bottom: 14px;
`;

const PillarTitle = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 14px;
  line-height: 1.22;
`;

const PillarBody = styled.p`
  font-size: 15px;
  line-height: 1.65;
  color: ${theme.colors.text.dark};
  margin: 0 0 22px;
`;

const PillarImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  display: block;
  margin-top: auto;
  background: ${theme.colors.white};
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid rgba(232, 123, 39, 0.18);
`;

const PillarImageStack = styled.div`
  margin-top: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  ${PillarImage} {
    aspect-ratio: 1 / 1;
    margin-top: 0;
  }
`;

/* ─── 4. DICKINSON STORY ─────────────────────────────────────────────────── */

const DickinsonSection = styled.section`
  background: linear-gradient(
    180deg,
    ${theme.colors.paleBeige} 0%,
    ${theme.colors.background} 100%
  );
  padding: 100px 0;

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const DickinsonHeader = styled.div`
  text-align: center;
  max-width: 820px;
  margin: 0 auto 48px;
`;

const DickinsonTag = styled(SectionTag)`
  color: ${theme.colors.orangeDark};
`;

const DickinsonTitle = styled.h2`
  font-size: 44px;
  font-weight: 500;
  font-style: italic;
  color: ${theme.colors.black};
  margin: 0 0 14px;
  line-height: 1.15;
  letter-spacing: -0.5px;

  @media ${MOBILE} {
    font-size: 32px;
  }
`;

const DickinsonMeta = styled.p`
  font-size: 12px;
  letter-spacing: 1.2px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin: 0;
`;

const DickinsonBodyWrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const DickinsonChapterTag = styled.h3`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.orange};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 40px 0 14px;

  &:first-child {
    margin-top: 0;
  }
`;

const DickinsonBody = styled.p`
  font-size: 17px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  margin: 0 0 18px;

  @media ${MOBILE} {
    font-size: 16px;
  }

  em {
    font-style: italic;
    color: ${theme.colors.black};
  }
`;

const DickinsonQuoteLayout = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 24px;
  align-items: stretch;
  margin: 36px 0;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const DickinsonPullQuote = styled.blockquote<{ $variant?: 'highlight' }>`
  position: relative;
  margin: 0;
  padding: 36px 40px 32px;
  border-radius: 16px;
  background: ${({ $variant }) =>
    $variant === 'highlight' ? theme.colors.orange : theme.colors.white};
  color: ${({ $variant }) =>
    $variant === 'highlight' ? theme.colors.white : theme.colors.black};
  box-shadow: 0 16px 40px rgba(26, 21, 48, 0.08);
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'highlight' ? 'rgba(255,255,255,0.25)' : 'rgba(232, 123, 39, 0.2)'};

  @media ${MOBILE} {
    padding: 28px 24px 24px;
  }
`;

const DickinsonQuoteMark = styled.span`
  display: block;
  font-size: 64px;
  line-height: 0.5;
  color: inherit;
  opacity: 0.4;
  margin-bottom: 12px;
`;

const DickinsonPullQuoteText = styled.p`
  font-size: 26px;
  font-weight: 500;
  line-height: 1.35;
  margin: 0 0 18px;

  @media ${MOBILE} {
    font-size: 21px;
  }
`;

const DickinsonPullQuoteAttrib = styled.cite`
  display: block;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.5px;
  opacity: 0.9;
`;

const DickinsonPortrait = styled.img`
  width: 50%;
  height: 50%;
  object-fit: cover;
  border-radius: 16px;
  background: ${theme.colors.white};
  box-shadow: 0 16px 40px rgba(26, 21, 48, 0.12);
  border: 1px solid rgba(232, 123, 39, 0.2);
  display: block;
`;

const FormatCallout = styled.div`
  background: ${theme.colors.paleBeige};
  border: 1px dashed rgba(232, 123, 39, 0.45);
  border-radius: 10px;
  padding: 20px 24px;
  margin: 24px 0 28px;
  font-style: italic;
  font-size: 17px;
  line-height: 1.6;
  color: ${theme.colors.black};
`;

/* ─── 5. PROOF / STATS STRIP ───────────────────────────────────────────────── */

const ProofSection = styled.section`
  background: #3f6b43;
  padding: 100px 0;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const ProofTitle = styled(SectionTitle)`
  color: ${theme.colors.white};
  margin-bottom: 48px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $highlight?: boolean }>`
  padding: 28px 16px;
  border-radius: 10px;
  text-align: center;
  height: 100%;
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(26, 21, 48, 0.08);
  }
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(232, 123, 39, 0.18)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid
    ${({ $highlight }) =>
      $highlight ? 'rgba(232, 123, 39, 0.55)' : 'rgba(255, 255, 255, 0.14)'};
`;

const StatBigNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 50px;
  font-weight: 900;
  line-height: 1;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  letter-spacing: -1px;

  @media ${MOBILE} {
    font-size: 42px;
  }
`;

const StatBigPct = styled.span`
  font-size: 26px;
  font-weight: 800;
`;

const StatArrow = styled.span`
  font-size: 30px;
  margin: 0 4px;
  opacity: 0.85;
`;

const StatCardLabel = styled.div`
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: ${theme.colors.white};
  margin-top: 14px;
  line-height: 1.35;
`;

const StatCardSub = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
`;

/* ─── 6. FINAL DUAL CTA ────────────────────────────────────────────────────── */

const FinalCtaSection = styled.section`
  background: ${theme.colors.paleBeige};
  padding: 100px 0;

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const FinalCtaInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const FinalCtaImageWrap = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 50px rgba(26, 21, 48, 0.18);
  aspect-ratio: 4 / 3;
  background: ${theme.colors.white};
`;

const FinalCtaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(232, 123, 39, 0.18);
`;

const FinalCtaContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const FinalCtaTitle = styled.h2`
  font-size: 44px;
  font-weight: 500;
  font-style: italic;
  color: ${theme.colors.black};
  margin: 0 0 36px;
  line-height: 1.15;
  letter-spacing: -0.5px;

  @media ${MOBILE} {
    font-size: 30px;
    margin-bottom: 28px;
  }
`;

const FinalCtaButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 28px 36px;
  margin-bottom: 32px;
`;

const FinalCtaGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const FinalCtaGroupLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${theme.colors.orangeDark};
  letter-spacing: 1.6px;
  text-transform: uppercase;
`;

const FinalCtaPrimaryButton = styled(Button)`
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};
  border-radius: 28px;
  padding: 0 32px;
  font-size: 16px;
  height: 52px;
  font-weight: 800;
  box-shadow: 0 6px 22px rgba(232, 123, 39, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    background-color: ${theme.colors.orangeDark};
    color: ${theme.colors.white};
    box-shadow: 0 10px 30px rgba(232, 123, 39, 0.45);
  }
`;

const FinalCtaSecondaryButton = styled(Button)`
  background-color: transparent;
  color: ${theme.colors.orangeDark};
  border: 2px solid ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 32px;
  font-size: 16px;
  height: 52px;
  font-weight: 800;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background-color: ${theme.colors.orangeDark};
    color: ${theme.colors.white};
  }
`;

const FinalCtaSignoff = styled.p`
  font-style: italic;
  font-size: 16px;
  color: ${theme.colors.text.dark};
  margin: 0;
  line-height: 1.5;
`;

const FinalCtaHeart = styled.span`
  font-style: normal;
`;
